import React from 'react';
import {
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from '@mui/styles';
import { Field } from 'redux-form';
import { renderDropdown, renderTextField } from 'components/form/field/redux-field';
import { required } from 'components/form/validation/validation';
import { renderTimePicker } from 'components/form/datepicker/datetime-picker';
import AutocompleteMinigrid from 'components/form/field/render-minigrid-autocomplete';
import { fetchUserData } from 'services/utils/therapy-service';
import { cpStyle } from '../../index.styles';
import { IFields } from '../../table/types';
import { ARBOR_TIME_ZONES } from '../../constants';

interface ISubFormProps {
  formTitle: string;
  allCustomers: { label: string; value: number }[];
  customerId: number;
  fieldsInfo: IFields[];
  setFieldValue: (field: string, value: any) => void;
}

export const PharmacyInformation: React.FC<ISubFormProps> = (props: ISubFormProps): JSX.Element => {
  const useStyles = makeStyles(cpStyle);
  const classes = useStyles();

  const { formTitle, allCustomers, customerId, fieldsInfo, setFieldValue } = props;
  const MAX_LENGTH_INPUT = '100';

  const handleUserList = (searchText: string) =>
    // eslint-disable-next-line no-confusing-arrow
    fetchUserData(searchText).then((result: any) =>
      result.data
        ? result.data.users.map((userData: any) => ({
            id: userData.id,
            label: userData.display_name,
          }))
        : [],
    );

  React.useEffect(() => {
    setFieldValue('site', customerId);
  }, [customerId]);

  React.useEffect(() => {
    if (fieldsInfo.length) {
      for (const fieldInfo of fieldsInfo) {
        if (fieldInfo.fieldName && fieldInfo.value !== ' - ') {
          setFieldValue(fieldInfo.fieldName, fieldInfo.value);
        }
      }
    }
  }, [fieldsInfo]);

  return (
    <Grid item xs={12}>
      <Accordion className={classes.accordionheader}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className={classes.expandIconColor} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography>
            <Tooltip title={formTitle} placement="right-end">
              <span className={classes.accordionTitle}>{`${formTitle}`}</span>
            </Tooltip>
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.AccordionDetailsForm}>
          <Grid container spacing={3} className={classes.rowMarginTop}>
            <Grid item xs={3}>
              <Field
                name="site"
                label="Site *"
                component={renderDropdown}
                // disabled={false}
                validate={[required]}
                fields={allCustomers}
              />
            </Grid>
            <Grid item xs={9}>
              <Field
                name="pi_dba"
                label="DBA *"
                component={renderTextField}
                // disabled={false}
                validate={[required]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={2}>
              <Field label="Hour Start" name="pi_hour_start" component={renderTimePicker} />
            </Grid>
            <Grid item xs={2}>
              <Field label="Hour End" name="pi_hour_end" component={renderTimePicker} />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_timezone"
                label="Timezone"
                component={renderDropdown}
                // disabled={false}
                validate={[]}
                fields={ARBOR_TIME_ZONES}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.subSectionTitle}>
            <Typography>
              <span>Pharmacy General Info</span>
            </Typography>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <Field
                name="pi_npi"
                label="NPI"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_dea"
                label="DEA"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_ncpdp"
                label="NCPDP (NABP)"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_state_license"
                label="State License #"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_tax_id"
                label="Tax ID (EIN) #"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_cost_center"
                label="Cost Center"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_psao"
                label="PSAO"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.subSectionTitle}>
            <Typography>
              <span>System Info</span>
            </Typography>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <Field
                name="pi_emr"
                label="EMR"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_dispensing_system"
                label="Dispensing System"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_pos"
                label="POS"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3} className={classes.rowMarginTop}>
            <Grid item xs={3}>
              <Field
                name="pi_sure_script_name"
                label="SureScripts Name"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_right_fax_lines"
                label="RightFax Lines"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_talkdesk_lines"
                label="Talkdesk Lines"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_printers"
                label="Printers"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.subSectionTitle}>
            <Typography>
              <span>Wholesale Info</span>
            </Typography>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <Field
                name="pi_wholesaler"
                label="Wholesaler"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_wholesaler_wac"
                label="Wholesaler (WAC)"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_wholesaler_340b"
                label="Wholesaler (340B)"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_wholesaler_website"
                label="Wholesaler Website"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.subSectionTitle}>
            <Typography>
              <span>Medicaid Info</span>
            </Typography>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <Field
                name="pi_medicaid_enrollment_unit"
                label="Medicaid Enrollment Unit"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_state_medicaid_number"
                label="State Medicaid Number"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="pi_medicaid_submitter_id"
                label="Medicaid Submitter ID"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.subSectionTitle}>
            <Typography>
              <span>Internal Operations</span>
            </Typography>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Field
                name="pi_pharmacy_staff"
                label="Pharmacy Staff:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="pi_pharmacy_operations"
                label="Supervisor, Pharmacy Operations:"
                disabled={false}
                component={AutocompleteMinigrid}
                validate={[]}
                fetchOptions={handleUserList}
                hint="Search by user name"
                minSearchLength={2}
                qaId="pi_pharmacy_operations_user_name"
                onChange={(user: any) => {
                  if (user) {
                    setFieldValue('pi_pharmacy_operations', user.id);
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="pi_clinical_pharmacist"
                label="Clinical Pharmacist:"
                disabled={false}
                component={AutocompleteMinigrid}
                validate={[]}
                fetchOptions={handleUserList}
                hint="Search by user name"
                minSearchLength={2}
                qaId="pi_clinical_pharmacist_user_name"
                onChange={(user: any) => {
                  if (user) {
                    setFieldValue('pi_clinical_pharmacist', user.id);
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="pi_pharmacy_technician"
                label="Pharmacy Technician:"
                disabled={false}
                component={AutocompleteMinigrid}
                validate={[]}
                fetchOptions={handleUserList}
                hint="Search by user name"
                minSearchLength={2}
                qaId="pi_pharmacy_technician_user_name"
                onChange={(user: any) => {
                  if (user) {
                    setFieldValue('pi_pharmacy_technician', user.id);
                  }
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};
